import React, { useMemo } from "react";
import FormControl from "@material-ui/core/FormControl";
import {
  StyledFormHelperText,
  useFormControlStyles,
  useStyledInputAdorment,
} from "./Input.style";
import InputAdornment from "@material-ui/core/InputAdornment";
import FilledInput from "@material-ui/core/FilledInput";
import OutlinedInput from "@material-ui/core/OutlinedInput";
import { IProps } from "./Input.interface";
import {
  useStyleFilledInput,
  useStyleOutlinedInput,
  useStyleLabelInput,
} from "../common.style";
import InputLabel from "@material-ui/core/InputLabel";

const Input: React.FC<IProps> = (props) => {
  const {
    error,
    fullWidth,
    id,
    label,
    errorMessage,
    errorId,
    unit,
    formControlMargin,
    unitPosition,
    color,
    disableUnderline,
    fontSize,
    fontWeight,
    fontColor,
    variant,
    testId,
    ...rest
  } = props;
  const { root, inputStyle } = useFormControlStyles({ formControlMargin });

  const fileldInputClasses = useStyleFilledInput({
    ...props,
    isLabeled: label ? true : false,
  });

  const outlinedInputClasses = useStyleOutlinedInput({
    ...props,
    isLabeled: label ? true : false,
  });

  const labelInputFilledClasses = useStyleLabelInput({
    ...props,
    inputVariant: "filled",
  });

  const labelInputOutlinedClasses = useStyleLabelInput({
    ...props,
    inputVariant: "outlined",
  });

  const inputAdornmentClasses = useStyledInputAdorment({
    ...props,
  });

  const unitProps = useMemo(() => {
    let props = {
      disableUnderline: error ? false : disableUnderline,
    } as any;
    if (unit) {
      if (unitPosition === "end") {
        props = {
          ...props,
          endAdornment: (
            <InputAdornment position="start" classes={inputAdornmentClasses}>
              {unit}
            </InputAdornment>
          ),
        };
      } else {
        props = {
          ...props,
          startAdornment: (
            <InputAdornment position="start" classes={inputAdornmentClasses}>
              {unit}
            </InputAdornment>
          ),
        };
      }
    }
    return props;
  }, [unit, unitPosition, error, disableUnderline, inputAdornmentClasses]);

  // memoize the input component
  const inputComponent = useMemo(() => {
    if (variant === "filled") {
      return (
        <FilledInput
          id={id}
          color={color}
          classes={fileldInputClasses}
          disableUnderline={disableUnderline}
          inputProps={{
            className: inputStyle,
          }}
          data-testid={`${
            testId ? `${testId}-filled-input` : "common-filled-input"
          }`}
          {...rest}
          {...unitProps}
        />
      );
    }
    return (
      <OutlinedInput
        id={id}
        color={error ? "primary" : color}
        classes={outlinedInputClasses}
        label={label}
        inputProps={{
          className: inputStyle,
        }}
        data-testid={`${
          testId ? `${testId}-outlined-input` : "common-outlined-input"
        }`}
        {...rest}
        {...unitProps}
      />
    );
  }, [
    color,
    disableUnderline,
    id,
    fileldInputClasses,
    outlinedInputClasses,
    rest,
    inputStyle,
    unitProps,
    variant,
    label,
    error,
    testId,
  ]);

  return (
    <FormControl
      variant="filled"
      error={error}
      fullWidth={fullWidth}
      classes={{ root }}
      data-testid={testId || "common-input-wrapper"}
    >
      {label && (
        <InputLabel
          variant={variant}
          error={error}
          color={color}
          htmlFor={id}
          classes={
            variant === "filled"
              ? labelInputFilledClasses
              : labelInputOutlinedClasses
          }
        >
          {label}
        </InputLabel>
      )}
      {inputComponent}
      {error && (
        <StyledFormHelperText variant="filled" id={errorId} error={true}>
          {errorMessage}
        </StyledFormHelperText>
      )}
    </FormControl>
  );
};

Input.defaultProps = {
  fullWidth: true,
  id: "input-initial-component",
  error: false,
  errorId: "component-error-text",
  errorMessage: "error",
  type: "text",
  disabled: false,
  disableUnderline: false,
  variant: "filled",
};

export default Input;
